import React, { memo, useMemo } from "react";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import { round } from "lodash";
import {
  getFormattedNumber,
  prettyNumericRound,
  getIndicators,
  orderStatus,
} from "../../../utils";
import { isItemDepartment } from "../../../utils/deptValidator";
import NumericLabel from "../../../components/NumericLabel";
import useStableCallback from "../../../hooks/useStableCallback";

const DeptPaperSx = (isSelected, isReleased) => ({
  marginBottom: "1px",
  py: "5px",
  backgroundColor: "",
  overflow: "none",
  boxShadow: getIndicators(false, false, isSelected, isReleased, false, false),
});

const CustomDeptSx = {
  fontSize: "0.75rem",
  paddingTop: "0px !important",
  alignSelf: "center",
  paddingLeft: "10px",
  paddingRight: "10px",

  "& .MuiCheckbox-root": {
    padding: "0px",
    paddingRight: "5px",
  },
  "& .dept-title": {
    display: "inline-flex",
    verticalAlign: "unset",

    "& .dept-name": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontWeight: 600,
    },
  },
  "& .bold-label": { fontWeight: 500 },
};

function CustomDepartment({
  handleSelect,
  freezeDept = false,
  hideSelection = false,
  data = {},
}) {
  const handleSelection = useStableCallback((dept) => (event) => {
    handleSelect(dept, event?.target?.checked);
  });

  const isSelected = useMemo(
    () => data?.SelectedPogs > 0,
    [data?.SelectedPogs]
  );

  const isReleased = useMemo(
    () => data?.ReleaseStatus === orderStatus.released,
    [data?.ReleaseStatus]
  );

  const isXDept = useMemo(
    () => isItemDepartment(data),
    [data?.DepartmentLetter]
  );

  return (
    <Paper sx={DeptPaperSx(isSelected, isReleased)}>
      <Grid
        container
        sx={CustomDeptSx}
        columnGap={0.5}
        alignItems="center"
        justifyContent="space-between"
      >
        {!hideSelection && (
          <Grid item xs="auto">
            <Checkbox
              size="small"
              disableRipple
              checked={data?.selection}
              onClick={handleSelection(data)}
              disabled={freezeDept}
            />
          </Grid>
        )}
        <Grid item xs={2.5} className="dept-title">
          <Tooltip title={data?.Department ?? ""} arrow>
            <Link
              href={`/manage-planograms/pogs/${data?.DepartmentLetter}`}
              className="dept-name"
            >
              {data?.Department}
            </Link>
          </Tooltip>
        </Grid>
        <Grid item xs={1.5}>
          {data?.SelectedPogs || 0}/{data?.TotalPogs}
        </Grid>
        <Grid item xs={1}>
          {isXDept ? "-" : data?.ExistingPogs || 0}
        </Grid>
        <Grid item xs={1}>
          {isXDept ? "-" : `${data?.LinealFootage || 0}ft`}
        </Grid>
        <Grid item xs={1}>
          {isXDept ? "-" : `${data?.AdjLineal || 0}ft`}
        </Grid>
        <Grid item xs={1}>
          <NumericLabel isSeparator value={round(data?.SkuQty || 0, 2)} />
        </Grid>
        <Grid item xs={1.5}>
          ${getFormattedNumber(prettyNumericRound(data?.AdjCost || 0))}
        </Grid>
        <Grid item xs={1.5}>
          ${getFormattedNumber(prettyNumericRound(data?.ExtendedCost || 0))}
        </Grid>
      </Grid>
    </Paper>
  );
}

export default memo(CustomDepartment);
